import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ApiTable from "../../@ui/ApiTable/ApiTable";
import {
  useLayerService,
  useOrderService,
  useUserAdminService,
} from "../../hooks";
import { ApiTableState } from "../../types/ApiTable/ApiTableState";
import { getUserDetails } from "../auth/AuthProvider";
import { Roles } from "../../constant/Roles";
import { Pagination } from "../../types/PaginiationResult";
import { OrderItemSummary } from "../../types/Order/orderItemSummary";
import { OrderLineItem } from "../../types/Order/OrderLineItem";
import { ApiTableColumn } from "../../types/ApiTable/ApiTableColumn";
import { FilterType } from "../../types/ApiTable/ColumnFilter";
import OrderItemsActionDropdown from "../admin/Orders/OrderItemsActionDropdown";
import OrderItemModal from "../modal/OrderItem/OderItemModal";
import ReOrderModal from "../modal/OrderItem/ReOrderModal";
import ManageRenewalModal from "../modal/OrderItem/ManageRenewal/ManageRenewalModal";
import DeleteDataModal from "../modal/OrderItem/DeleteDataModal";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import {
  WfsBadRequestResponse,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsPaymentRequiredResponse,
} from "../../types/Wfs/WfsResponse";
import Price from "../../@ui/Price";
import ToggleSwitch from "../../@ui/ToggleSwitch";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { useTenantContext } from "../../context/Tenant/TenantContext";
import { useAuthContext } from "../auth/AuthContext";

interface ExtendedTableState {
  state: ApiTableState;
  isPremium: boolean;
  excludeTraccOrders: boolean;
  showInrixRenewals: boolean;
}

interface OrderItemModalProps {
  orderItemSummary: OrderItemSummary;
  wfsParameters: any;
}

interface ReOrderModalProps {
  isLoading: boolean;
  orderItem: OrderItemSummary;
  wfsParameters: any;
  result?:
    | WfsDataResponse
    | WfsDeferredRequestResponse
    | WfsPaymentRequiredResponse
    | WfsBadRequestResponse;
}

interface UserOrdersProps {
  isMyOrders?: boolean;
}

const UserOrders = ({ isMyOrders = false }: UserOrdersProps) => {
  const orderService = useOrderService();
  const layerService = useLayerService();
  const userAdminService = useUserAdminService();
  const { config } = useTenantContext();
  const { user } = useAuthContext();

  const currentUserId = user?.id;

  const [searchParams, setSearchParams] = useSearchParams();

  const hideTraccToggle = config.hideFeatures?.includes("tracc") ?? false;
  const hidePremiumToggle =
    config.hideFeatures?.includes("freeOrders") ?? false;

  const isAdmin = getUserDetails()?.roles.includes(Roles.ADMINISTRATOR);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [layerOptions, setLayerOptions] = useState<
    {
      label: string;
      value: number;
    }[]
  >([]);
  const [extendedState, setExtendedState] = useState<ExtendedTableState>({
    state: {
      pagination: {
        page: 1,
      },
    },
    isPremium: false,
    excludeTraccOrders: true,
    showInrixRenewals: false,
  });
  const [orderItems, setOrderItems] = useState<Pagination<OrderItemSummary>>({
    records: [],
    totalPages: 1,
    page: 1,
  });
  const [modalData, setModalData] = useState<OrderItemModalProps | null>(null);
  const [reOrderData, setReOrderData] = useState<ReOrderModalProps | null>(
    null,
  );
  const [manageRenewalItem, setManageRenewalItem] = useState<{
    summary: OrderItemSummary;
    lineItem: OrderLineItem;
  } | null>(null);
  const [deleteDataItem, setDeleteDataItem] = useState<OrderItemSummary | null>(
    null,
  );

  const [tryCount, setTryCount] = useState(0);

  const tryCounter = () => {
    setTimeout(() => {
      setTryCount((prevCount) => prevCount + 1);
    }, 15000);
  };

  useEffect(() => {
    layerService
      .ListAvailable()
      .then((layers) => {
        setLayerOptions(
          layers.map((layer) => {
            return {
              label: layer.displayName,
              value: layer.id,
            };
          }),
        );
      })
      .catch(() => {});
  }, [layerService]);

  const listOrderItems = useCallback(
    (abortController: AbortController) => {
      const { state, isPremium, excludeTraccOrders, showInrixRenewals } =
        extendedState;
      const filterExpression = getCombinedFilterExpression(
        isMyOrders ? undefined : state.filterExpression,
        isPremium,
        excludeTraccOrders,
        showInrixRenewals,
        isMyOrders ? currentUserId : undefined,
      );
      const sortExpression =
        state.sort ? `${state.sort.field} ${state.sort.direction}` : undefined;

      orderService
        .ListOrderItems(
          {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
            filterExpression,
            sortExpression,
          },
          abortController,
        )
        .then((r) => {
          const inProgress = r.records.some(
            (item) => item.status === "In Progress" || item.status === "Queued",
          );

          if (inProgress && extendedState.state.pagination.page === 1) {
            tryCounter();
          }

          setOrderItems(r);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [extendedState, setLoading, setOrderItems, tryCounter],
  );

  useEffect(() => {
    const abortController = new AbortController();
    listOrderItems(abortController);
    return () => {
      abortController.abort();
    };
  }, [tryCount]);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    listOrderItems(abortController);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendedState]);

  const getCombinedFilterExpression = (
    filter: string | undefined,
    isPremium: boolean,
    excludeTraccOrders: boolean,
    showInrixRenewals: boolean,
    userId?: string,
  ) => {
    let combinedFilter = "";
    if (filter) combinedFilter = filter;

    if (userId)
      combinedFilter =
        combinedFilter === "" ?
          `UserId eq ${userId}`
        : `${combinedFilter} _AND UserId eq ${userId}`;

    if (isPremium)
      combinedFilter =
        combinedFilter === "" ? "isPremium eq true" : (
          `${combinedFilter} _AND isPremium eq true`
        );

    if (excludeTraccOrders)
      combinedFilter =
        combinedFilter === "" ? "UserAgent <> TRACC" : (
          `${combinedFilter} _AND UserAgent <> TRACC`
        );

    if (showInrixRenewals)
      combinedFilter =
        combinedFilter === "" ? "" : (
          `${combinedFilter} _AND Tenant eq Inrix _AND HasRenewal eq true`
        );

    return combinedFilter;
  };

  const onStateChange = (tableState: ApiTableState) => {
    setExtendedState({ ...extendedState, state: tableState });
  };

  const isPremiumChange = (isPremium: boolean) => {
    const updatedState = extendedState;
    updatedState.isPremium = isPremium;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const excludeTraccOrdersChange = (excludeTraccOrders: boolean) => {
    const updatedState = extendedState;
    updatedState.excludeTraccOrders = excludeTraccOrders;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const showInrixRenewalsChange = (showInrixRenewals: boolean) => {
    const updatedState = extendedState;
    updatedState.showInrixRenewals = showInrixRenewals;
    updatedState.state.pagination.page = 1;
    setExtendedState({ ...updatedState });
  };

  const onDownloadClick = (orderItem: OrderItemSummary) => {
    const url = `/download/data/${orderItem.orderId}/${orderItem.orderItemId}`;
    window.open(url, "_blank");
  };

  const viewInvoice = (orderItem: OrderItemSummary) => {
    window.open(orderItem.invoiceUrl, "_blank");
  };

  const viewDetails = (item: OrderItemSummary) => {
    setModalLoading(true);
    orderService
      .GetOrderItem(item.orderId, item.orderItemId)
      .then((data) => {
        setModalData({
          orderItemSummary: item,
          wfsParameters: data?.parameters,
        });
      })
      .catch(() => {})
      .finally(() => {
        setModalLoading(false);
      });
  };

  useEffect(() => {
    if (reOrderData == null || reOrderData?.isLoading === false) {
      return;
    }

    orderService
      .ReOrder(
        reOrderData.orderItem.orderId,
        reOrderData.orderItem.orderItemId,
        reOrderData.orderItem.transactionCurrencyCode,
      )
      .then((response) => {
        const r = response as any;
        setReOrderData({
          ...reOrderData!,
          orderItem: {
            ...reOrderData!.orderItem,
            orderItemId: r.id,
          },
          isLoading: false,
          result: response,
        });
      })
      .catch(() => {});
  }, [reOrderData]);

  const reOrder = (item: OrderItemSummary) => {
    setModalLoading(true);
    orderService
      .GetOrderItem(item.orderId, item.orderItemId)
      .then((data) => {
        setReOrderData({
          isLoading: true,
          orderItem: item,
          wfsParameters: data?.parameters,
        });
      })
      .catch(() => {})
      .finally(() => {
        setModalLoading(false);
      });
  };

  const closeReOrder = () => {
    setReOrderData(null);
    setExtendedState({
      state: {
        pagination: {
          page: 1,
        },
      },
      isPremium: false,
      excludeTraccOrders: true,
      showInrixRenewals: false,
    });
  };

  useEffect(() => {
    const action = searchParams.get("action");
    const orderItemId = searchParams.get("orderItemId");
    const orderId = searchParams.get("orderId");

    if (action !== "reorder" || !orderItemId || !orderId) return;

    setSearchParams({}, { replace: true });
    setModalLoading(true);
    orderService
      .GetOrderById(orderId)
      .then((order) => {
        const lineItem = order.lineItems.find((li) => li.id === orderItemId);
        if (!lineItem) return;

        const orderItemSummary: OrderItemSummary = {
          orderItemId,
          orderId,
          transactionCurrencyCode: order.transactionCurrencyCode,
          product: lineItem.layerName,
          orderDate: order.createdDate,
          priceExTax: lineItem.priceExTax,
          priceIncTax: lineItem.priceIncTax,
          isPremium: lineItem.isPremium,
          taxCode: lineItem.taxCode,
          dataExpiry: order.createdDate,
          status: "",
          userFullName: "",
          layerName: lineItem.layerName,
        };

        setReOrderData({
          isLoading: true,
          orderItem: orderItemSummary,
          wfsParameters: lineItem.parameters,
        });
      })
      .catch(() => {})
      .finally(() => {
        setModalLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const action = searchParams.get("action");
    const orderItemId = searchParams.get("orderItemId");

    if (action !== "delete" || !orderItemId) return;

    setSearchParams({}, { replace: true });
    setModalLoading(true);
    orderService
      .GetOrderItemById(orderItemId)
      .then((lineItem) => {
        const orderItemSummary: OrderItemSummary = {
          orderItemId,
          orderId: "",
          product: lineItem.layerName,
          orderDate: DateTime.now(),
          priceExTax: lineItem.priceExTax,
          priceIncTax: lineItem.priceIncTax,
          transactionCurrencyCode: lineItem.transactionCurrencyCode,
          taxCode: lineItem.taxCode,
          isPremium: lineItem.isPremium,
          dataExpiry: DateTime.now(),
          status: "",
          userFullName: "",
          layerName: lineItem.layerName,
        };
        setDeleteDataItem(orderItemSummary);
      })
      .catch(() => {})
      .finally(() => {
        setModalLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manageRenewal = (item: OrderItemSummary) => {
    setModalLoading(true);
    orderService
      .GetOrderItemById(item.orderItemId)
      .then((lineItem) => {
        setManageRenewalItem({ summary: item, lineItem });
      })
      .catch(() => {})
      .finally(() => {
        setModalLoading(false);
      });
  };

  const onDeleteDataConfirm = (item: OrderItemSummary) => {
    orderService
      .SetOrderItemDataDeleted(item.orderItemId)
      .then(() => {
        setDeleteDataItem(null);
        setExtendedState((prev) => ({
          ...prev,
          state: { ...prev.state, pagination: { page: 1 } },
        }));
      })
      .catch(() => {});
  };

  const cols = [
    {
      field: "userFullName",
      label: "User",
      renderContents: (value: OrderItemSummary) => (
        <>{`${value.userFullName}`}</>
      ),
      sortable: true,
      hide: !isAdmin || isMyOrders,
      filter: {
        type: FilterType.TYPEAHEAD,
        filterKey: "userId",
        typeaheadOptions: {
          optionLabelKey: (option: any) =>
            `${option.fullName} - ${option.email}`,
          optionValueKey: "userId",
          onSearch: (searchTerm: string) =>
            userAdminService.searchUsers(searchTerm),
        },
      },
    },
    {
      field: "orderDate",
      label: "Order Date",
      renderContents: (value: OrderItemSummary) => (
        <>{`${value.orderDate.toFormat("dd/MM/yyyy HH:mm")}`}</>
      ),
      sortable: true,
      filter: {
        type: FilterType.DATE_RANGE,
        field: "orderDate",
      },
      style: { width: "10%" },
    },
    {
      field: "product",
      label: "Product",
      renderContents: (value: OrderItemSummary) => <>{`${value.product}`}</>,
      sortable: true,
      filter: {
        type: FilterType.SELECT,
        filterKey: "layerId",
        selectOptions: {
          options: layerOptions,
          operator: "eq",
        },
      },
      style: { width: "15%" },
    },
    {
      field: "area",
      label: "Area",
      renderContents: (value: OrderItemSummary) => (
        <span className="text-capitalize">{`${value.area}`}</span>
      ),
      style: { width: "15%" },
    },
    {
      field: "Status",
      label: "Status",
      sortable: true,
      filter: {
        type: FilterType.SELECT,
        filterKey: "Status",
        selectOptions: {
          options: [
            { label: "Expired", value: "Expired" },
            { label: "Complete", value: "Complete" },
            { label: "Ready to download", value: "Ready to download" },
            { label: "In Progress", value: "In Progress" },
            { label: "Queued", value: "Queued" },
            { label: "Pending Payment", value: "Pending Payment" },
            { label: "Failed", value: "Failed" },
          ],
          operator: "eq",
        },
      },
      renderContents: (value: OrderItemSummary) => (
        <>
          {`${value.status}`}
          {value.status === "In Progress" && value.progress !== undefined ?
            ` - ${Math.round(value.progress * 100)}%`
          : ""}
        </>
      ),
      style: { width: "10%" },
    },
    {
      field: "dataExpiry",
      label: "Expires",
      sortable: true,
      renderContents: (value: OrderItemSummary) => {
        if (!value.dataExpiry) return <></>;
        const now = DateTime.now();
        const isExpired = value.dataExpiry < now;
        const isNearExpiry =
          !isExpired && value.dataExpiry < now.plus({ months: 1 });
        return (
          <span className="d-flex align-items-center gap-2">
            {value.dataExpiry.toFormat("dd/MM/yyyy")}
            {isExpired && (
              <span title="Expired">
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ color: "red" }}
                />
              </span>
            )}
            {isNearExpiry && (
              <span title="Expires soon">
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ color: "orange" }}
                />
              </span>
            )}
          </span>
        );
      },
      style: { width: "10%" },
    },
    {
      field: "value",
      label: "Value",
      renderContents: (value: OrderItemSummary) =>
        value.priceExTax ?
          <div className="w-100 d-flex flex-row justify-content-end">
            <Price
              currencyCode={value.transactionCurrencyCode}
              price={value.priceExTax}
            />
          </div>
        : "",
      sortable: true,
      style: { width: "5%" },
    },
    {
      renderContents: (value: OrderItemSummary) => (
        <div className="w-100 h-100 d-flex flex-row justify-content-center">
          <OrderItemsActionDropdown
            orderItem={value}
            isAdmin={!!isAdmin}
            isMyOrders={isMyOrders}
            hideManageRenewal={isMyOrders}
            viewDetails={viewDetails}
            onDownloadClick={onDownloadClick}
            viewInvoice={viewInvoice}
            reOrder={reOrder}
            manageRenewal={manageRenewal}
            deleteData={isMyOrders ? setDeleteDataItem : undefined}
          />
        </div>
      ),
      style: { width: "4%" },
    },
  ] as ApiTableColumn[];

  const controls = useMemo(() => {
    if (isMyOrders) return null;

    return (
      <>
        {!hidePremiumToggle && (
          <ToggleSwitch
            id="premiumOnly"
            label="Show Premium Orders Only"
            value={extendedState.isPremium}
            onChange={isPremiumChange}
          />
        )}

        {!hideTraccToggle && (
          <ToggleSwitch
            id="excludeTracc"
            label="Exclude Tracc Orders"
            value={extendedState.excludeTraccOrders}
            onChange={excludeTraccOrdersChange}
          />
        )}

        <ToggleSwitch
          id="showInrixRenewals"
          label="Show Inrix Renewals"
          value={extendedState.showInrixRenewals}
          onChange={showInrixRenewalsChange}
        />
      </>
    );
  }, [
    isMyOrders,
    hidePremiumToggle,
    hideTraccToggle,
    extendedState.isPremium,
    extendedState.excludeTraccOrders,
    extendedState.showInrixRenewals,
    isPremiumChange,
    excludeTraccOrdersChange,
    showInrixRenewalsChange,
  ]);

  return (
    <>
      <ApiTable
        state={extendedState.state}
        data={orderItems}
        columns={cols}
        pageOptions={[10, 20, 50]}
        onChange={onStateChange}
        allowRefresh={true}
        loading={loading}
        controls={controls}
      />

      {modalLoading && (
        <div className="modal fade show d-block">
          <LoadingSpinner />
        </div>
      )}
      {modalData && (
        <OrderItemModal
          orderItemSummary={modalData.orderItemSummary}
          wfsParameters={modalData.wfsParameters}
          onDownloadClick={onDownloadClick}
          viewInvoice={viewInvoice}
          onClose={() => setModalData(null)}
        />
      )}
      {reOrderData && (
        <ReOrderModal
          isLoading={reOrderData.isLoading}
          orderItem={reOrderData.orderItem}
          wfsParameters={reOrderData.wfsParameters}
          result={reOrderData.result}
          onClose={closeReOrder}
        />
      )}
      {manageRenewalItem && (
        <ManageRenewalModal
          orderItem={manageRenewalItem.summary}
          lineItem={manageRenewalItem.lineItem}
          onClose={() => setManageRenewalItem(null)}
        />
      )}
      {deleteDataItem && (
        <DeleteDataModal
          orderItem={deleteDataItem}
          onConfirm={onDeleteDataConfirm}
          onClose={() => setDeleteDataItem(null)}
        />
      )}
    </>
  );
};

export default UserOrders;
